/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import Media from 'react-media';

import type UserAgent from 'js/lib/useragent';

import { breakpoints } from '@coursera/cds-core';
import type { CSSProperties } from '@coursera/coursera-ui';

import CertificateNavItem from 'bundles/premium-certificate/components/mastertrack/CertificateNavItem';
import Container from 'bundles/xddp/components/shared/Container';

import _t from 'i18n!nls/premium-certificate';

type Props = {
  pageSections: Array<{
    id: string;
    label: string;
  }>;
  isScrollPastBanner: boolean;
  isEmbeddedRegFormDisplayed: boolean;
  userAgent?: UserAgent;
};

type NavigationBarProps = {
  isStickyBarPresent: boolean;
  isEmbeddedRegFormDisplayed: boolean;
  isDesktopScreen: boolean;
};

const styles: Record<string, CSSProperties> = {
  navItemsList: {
    listStyleType: 'none',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    paddingLeft: 0,
    marginBottom: 0,
  },
  navigationBar: (
    isScrollPastBanner: $TSFixMe,
    isEmbeddedRegFormDisplayed: $TSFixMe,
    isDesktopScreen: NavigationBarProps
  ) => ({
    borderBottom: '1px solid var(--cds-color-grey-25)',
    ...(isScrollPastBanner && {
      position: 'fixed',
      width: '100%',
      background: 'var(--cds-color-neutral-background-primary)',
      // Chose 5 because some z-indexs in page content have a 3 z-index
      zIndex: 5,
      top: isDesktopScreen && !isEmbeddedRegFormDisplayed ? '72px' : 0,
    }),
  }),
};

const CertificateNavigationBar = ({
  pageSections,
  isScrollPastBanner,
  isEmbeddedRegFormDisplayed,
  userAgent,
}: Props) => {
  const [activeSection, setActiveSection] = useState(pageSections[0].id);

  useEffect(() => {
    const onScroll = () => {
      const { scrollY } = window;
      const stickyHeaderEl = document.querySelector('.rc-StickyHeader');
      const headerMargin = stickyHeaderEl ? 132 : 60;

      // Iterate through all of the section elements and figure out which section is currently in view
      // based on our scroll position.
      const activeState = pageSections.find(({ id }, index) => {
        const nextSectionName = pageSections[index + 1]?.id;

        const el: HTMLElement | null = document.querySelector(`#${id}`);
        const nextEl: HTMLElement | null = nextSectionName ? document.querySelector(`#${nextSectionName}`) : null;

        if (!el) {
          return false;
        }

        const isScrollPastElement = scrollY >= el.offsetTop - headerMargin;
        const isScrollPastNextElement = nextEl ? scrollY >= nextEl.offsetTop - headerMargin : false;

        const isSectionActive = isScrollPastElement && !isScrollPastNextElement;

        return isSectionActive;
      });

      // If no sections are "in view" (for example, at the top of the page we are technically not "at" the
      // first section yet), default to making the first section active.
      const sectionToSetActive = activeState?.id || pageSections[0].id;

      if (sectionToSetActive) {
        setActiveSection(sectionToSetActive);
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [pageSections]);

  return (
    <Media query={{ minWidth: breakpoints.values.md }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <div
          data-test="CertificateNavigationBar"
          css={styles.navigationBar(isScrollPastBanner, isEmbeddedRegFormDisplayed, matches)}
        >
          <Container component="nav" ariaLabel={_t('Certificate')}>
            <ul css={styles.navItemsList} aria-label={_t('Certificate Menu')}>
              {pageSections.map((section) => (
                <CertificateNavItem
                  key={section.id}
                  id={section.id}
                  label={section.label}
                  isActiveItem={activeSection === section.id}
                />
              ))}
            </ul>
          </Container>
        </div>
      )}
    </Media>
  );
};

export default CertificateNavigationBar;
